import type { MouseEventHandler } from 'react';

import ActionButton from 'components/ActionButton';
import Icon from 'components/Icon';
import Img from 'components/Img';
import Link from 'components/Link';
import OptionalContainer from 'components/OptionalContainer';
import Price, { BasePriceText } from 'components/Price';
import ProductTag from 'components/ProductTag';
import Rating from 'components/Rating';
import Text from 'components/Text';
import type { VariantStockResponse } from 'models/api';
import type { ProductPrice } from 'models/price';
import type { ProductTag as ProductTagModel, VariantId } from 'models/product';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import { useProductCardScreenReaderText } from './helpers';
import ProductCardStockInfo, { type StockInfo } from './ProductCardStockInfo';
import ProductCardTags from './ProductCardTags';
import ProductCardTechnicalInfo from './ProductCardTechnicalInfo';

export interface Props {
	addToCartDisabled?: boolean;

	addToWishlistDisabled?: boolean;

	/** Base price if the main price has a discount */
	basePrice?: ProductPrice;

	/** The list of items from checklist */
	checklist?: string[];

	/** Extra container class names */
	className?: string;

	/** Energy Symbol */
	energySymbolAlt?: string;

	/** Energy Symbol */
	energySymbolSrc?: string;

	/** Product tag in the Flag section */
	flag?: ProductTagModel;

	/** The heading in the component */
	heading?: string;

	/** The link to the product page */
	href: string;

	/** Image alt text */
	imageAlt: string | undefined;

	/** Image src attribute */
	imageSrc: string | undefined;

	/** If the card should have an even height with its neighbors. */
	isEvenHeight?: boolean;

	/** Stock object for stock section stock info */
	legacyStockInfo: StockInfo | undefined;

	/** Product has multiple variants */
	multipleVariantIndicatorText?: string;

	/** Add to cart callback */
	onAddToCartClick?: MouseEventHandler<HTMLButtonElement>;

	/** Add to wishlist callback */
	onAddToWishlistClick?: MouseEventHandler<HTMLButtonElement>;

	/** Link click callback */
	onLinkClick?: MouseEventHandler<HTMLAnchorElement>;

	/** The main price to display */
	price: ProductPrice | undefined;

	/** Link to product sheet */
	productSheetLink?: string;

	/** Product sheet link text */
	productSheetLinkDescription?: string;

	/** Sets the number of reviews */
	reviews?: number;

	/** Sets the numbers of score/stars */
	score?: number;

	/** Show and hide options for the button */
	showAddToCartButton?: boolean;

	/** Show and hide options for the button */
	showAddToWishlistButton?: boolean;

	/** Screen reader subheading as the visible might include nonsense characters */
	srSubheading?: string;

	stock?: VariantStockResponse;

	/** Display information related to store */
	storesEnabled: boolean;

	/** The sub heading in the component */
	subheading?: string;

	/** Product tags in the Product or Price sections */
	tags?: ProductTagModel[];

	/** Product tags in the Usp section */
	uspTags?: ProductTagModel[];

	/** The product's unique ID */
	variantId: VariantId;

	/** Vat info price text */
	vatPriceText?: string;
}

export default function ProductCardVertical({
	addToCartDisabled,
	addToWishlistDisabled,
	basePrice,
	checklist,
	className,
	energySymbolAlt,
	energySymbolSrc,
	flag,
	heading,
	href,
	imageAlt,
	imageSrc,
	isEvenHeight = false,
	legacyStockInfo,
	multipleVariantIndicatorText,
	onAddToCartClick,
	onAddToWishlistClick,
	onLinkClick,
	price,
	productSheetLink,
	productSheetLinkDescription,
	reviews,
	score = 0,
	showAddToCartButton = false,
	showAddToWishlistButton = false,
	srSubheading,
	stock,
	storesEnabled,
	subheading,
	tags,
	uspTags,
	variantId,
	vatPriceText,
}: Props) {
	const { t } = useI18n();
	const hasReviews = score > 0;
	const hasTags = is.arrayWithLength(tags);
	const customisableUspTag = uspTags?.find(
		({ type }) => type === 'Customizable',
	);

	const screenReaderText = useProductCardScreenReaderText({
		basePrice,
		customisableUspTag,
		checklist,
		flag,
		heading,
		multipleVariantIndicatorText,
		price,
		reviewCount: reviews,
		score,
		srSubheading,
		subheading,
		tags,
		vatPriceText,
	});

	const hasWishlistButton = !!(showAddToWishlistButton && onAddToWishlistClick);
	const hasCartButton = !!(showAddToCartButton && onAddToCartClick);

	return (
		<article
			className={cn(
				className,
				'relative w-full md:w-auto',
				isEvenHeight && 'flex flex-col',
			)}
			aria-label={heading}
		>
			<Link
				href={href}
				onClick={onLinkClick}
				className="group/product-card block"
				aria-label={screenReaderText}
			>
				{flag && (
					<ProductTag
						type={flag.type}
						text={flag.text}
						size="md"
						className="absolute top-0 left-0 z-1"
						aria-hidden
					/>
				)}
				<OptionalContainer
					// Normally an unnecessary optimization to be stringent about a
					// wrapping div, but there can be a lot of product cards on a page
					// and the site's DOM size is a problem.
					as="div"
					hasContainer={hasTags}
					className="relative"
					aria-hidden
				>
					<Img
						src={imageSrc}
						alt={imageAlt}
						width={160}
						height={160}
						className="block object-contain sm:max-w-[60%] md:h-40 md:max-w-none"
					/>
					{tags && (
						<ProductCardTags
							tags={tags}
							className="absolute bottom-0 left-0 z-1"
						/>
					)}
				</OptionalContainer>

				<header className="mt-2" aria-hidden>
					{heading && (
						<Text
							as="p"
							className="font-bold group-hover/product-card:underline"
						>
							{heading}
						</Text>
					)}
					{subheading && (
						<>
							{srSubheading && <p className="sr-only">{srSubheading}</p>}
							<Text as="pSmall" aria-hidden={srSubheading ? 'true' : undefined}>
								{subheading}
							</Text>
						</>
					)}
				</header>

				{hasReviews && (
					<Rating
						size="small"
						displayMode="block"
						className="mt-2"
						score={score}
						reviewCount={reviews}
						aria-hidden
					/>
				)}

				{price && (
					<div className="mt-2" aria-hidden>
						<Price price={price} />
						<Text text={vatPriceText} as="pXSmall" />
						<BasePriceText
							basePrice={basePrice}
							displayedPriceType={price.priceType}
							recentLowestPriceKey="product_list_recent_lowest_price_text"
							className="mt-1 text-xs"
						/>
					</div>
				)}

				{customisableUspTag?.text && (
					<Text
						as="pSmall"
						className="mt-3 flex items-center gap-1"
						aria-hidden
					>
						<Icon name="add-print" />
						{customisableUspTag.text}
					</Text>
				)}

				{checklist && (
					<ul
						className="mt-4 ml-4 flex list-outside list-disc flex-col gap-1 text-sm"
						aria-hidden
					>
						{checklist.map((item) => (
							<li key={item}>{item}</li>
						))}
					</ul>
				)}
			</Link>

			<ProductCardTechnicalInfo
				className="mt-4"
				energySymbolAlt={energySymbolAlt}
				energySymbolSrc={energySymbolSrc}
				href={href}
				onLinkClick={onLinkClick}
				productSheetLink={productSheetLink}
				productSheetLinkDescription={productSheetLinkDescription}
			/>

			{!multipleVariantIndicatorText && (
				<ProductCardStockInfo
					storesEnabled={storesEnabled}
					stockInfo={legacyStockInfo}
					stock={stock}
					variantId={variantId}
					className={cn('pt-4', isEvenHeight && 'mt-auto')}
				/>
			)}

			{multipleVariantIndicatorText && (
				<p
					className={cn(
						'flex items-center pt-4 text-xs',
						isEvenHeight && 'mt-auto',
					)}
					aria-hidden
				>
					<Link
						isScreenReaderHidden
						href={href}
						onClick={onLinkClick}
						className="hover:underline"
					>
						<Icon
							size={16}
							name="circle-chevron-right"
							color="brand"
							className="mr-1"
						/>
						{multipleVariantIndicatorText}
					</Link>
				</p>
			)}

			<OptionalContainer
				as="div"
				hasContainer={isEvenHeight && hasCartButton && hasWishlistButton}
			>
				{hasWishlistButton && (
					<ActionButton
						className={cn(
							'mt-4',
							isEvenHeight && !hasCartButton && 'self-start',
						)}
						size="sm"
						variant="secondary"
						leadingIcon="shopping-list"
						hasVisibleText={false}
						disabled={addToWishlistDisabled}
						onClick={onAddToWishlistClick}
						minimunLoadingTime={1000}
					>
						{t('general_add_to_wishlist_button_text')}
					</ActionButton>
				)}
				{hasCartButton && (
					<ActionButton
						className={cn(
							'mt-4',
							hasWishlistButton && 'ml-2',
							isEvenHeight && !hasWishlistButton && 'self-start',
						)}
						size="sm"
						variant="cta"
						leadingIcon="cart"
						hasVisibleText={false}
						disabled={addToCartDisabled}
						onClick={onAddToCartClick}
						minimunLoadingTime={1000}
					>
						{t('general_add_to_cart_button_text')}
					</ActionButton>
				)}
			</OptionalContainer>
		</article>
	);
}
ProductCardVertical.displayName = 'ProductCardVertical';
